// API Service for MediCare Plus
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper function for API calls
async function apiCall(endpoint, options = {}) {
  const url = `${API_URL}${endpoint}`;
  const defaultHeaders = {
    'Content-Type': 'application/json',
  };
  
  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }
    return response.json();
  } catch (error) {
    console.error(`API Error [${endpoint}]:`, error);
    throw error;
  }
}

// ============== PRODUCTS API ==============
export const productsApi = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value);
      }
    });
    const query = queryParams.toString();
    return apiCall(`/api/products${query ? `?${query}` : ''}`);
  },
  
  getById: (id) => apiCall(`/api/products/${id}`),
  
  getBySlug: (slug) => apiCall(`/api/products/slug/${slug}`),
  
  getFeatured: () => apiCall('/api/products?is_featured=true&is_active=true&limit=12'),
  
  getBestSellers: () => apiCall('/api/products?sort_by=sold_count&sort_order=desc&is_active=true&limit=8'),
  
  search: (query, filters = {}) => {
    const params = { search: query, is_active: true, ...filters };
    return productsApi.getAll(params);
  },
};

// ============== CATEGORIES API ==============
export const categoriesApi = {
  getAll: (isActive = true) => apiCall(`/api/products/categories${isActive !== null ? `?is_active=${isActive}` : ''}`),
  
  getById: (id) => apiCall(`/api/products/categories/${id}`),
};

// ============== CART API ==============
export const cartApi = {
  get: (sessionId, customerId = null) => {
    const params = customerId ? `?customer_id=${customerId}` : '';
    return apiCall(`/api/cart/${sessionId}${params}`);
  },
  
  addItem: (productId, quantity = 1, sessionId = null) => {
    return apiCall('/api/cart/add', {
      method: 'POST',
      body: JSON.stringify({ product_id: productId, quantity, session_id: sessionId }),
    });
  },
  
  updateItem: (sessionId, productId, quantity, customerId = null) => {
    const params = customerId ? `?customer_id=${customerId}` : '';
    return apiCall(`/api/cart/${sessionId}/item/${productId}${params}`, {
      method: 'PUT',
      body: JSON.stringify({ quantity }),
    });
  },
  
  removeItem: (sessionId, productId, customerId = null) => {
    const params = customerId ? `?customer_id=${customerId}` : '';
    return apiCall(`/api/cart/${sessionId}/item/${productId}${params}`, {
      method: 'DELETE',
    });
  },
  
  applyCoupon: (sessionId, couponCode, customerId = null) => {
    const params = customerId ? `?customer_id=${customerId}` : '';
    return apiCall(`/api/cart/${sessionId}/coupon${params}`, {
      method: 'POST',
      body: JSON.stringify({ coupon_code: couponCode }),
    });
  },
  
  removeCoupon: (sessionId, customerId = null) => {
    const params = customerId ? `?customer_id=${customerId}` : '';
    return apiCall(`/api/cart/${sessionId}/coupon${params}`, {
      method: 'DELETE',
    });
  },
  
  checkout: (sessionId, checkoutData) => {
    return apiCall(`/api/cart/${sessionId}/checkout`, {
      method: 'POST',
      body: JSON.stringify(checkoutData),
    });
  },
  
  clear: (sessionId, customerId = null) => {
    const params = customerId ? `?customer_id=${customerId}` : '';
    return apiCall(`/api/cart/${sessionId}${params}`, {
      method: 'DELETE',
    });
  },
};

// ============== ORDERS API ==============
export const ordersApi = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value);
      }
    });
    const query = queryParams.toString();
    return apiCall(`/api/orders${query ? `?${query}` : ''}`);
  },
  
  getById: (id) => apiCall(`/api/orders/${id}`),
  
  getByOrderNumber: (orderNumber) => apiCall(`/api/orders/number/${orderNumber}`),
  
  getCustomerOrders: (customerId) => apiCall(`/api/orders?customer_id=${customerId}`),
  
  create: (orderData) => apiCall('/api/orders', {
    method: 'POST',
    body: JSON.stringify(orderData),
  }),
  
  updateStatus: (orderId, status) => apiCall(`/api/orders/${orderId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  }),
};

// ============== CUSTOMERS/AUTH API ==============
export const authApi = {
  login: (email, password) => apiCall('/api/customers/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  }),
  
  register: (userData) => apiCall('/api/customers/auth/register', {
    method: 'POST',
    body: JSON.stringify(userData),
  }),
  
  getProfile: (customerId) => apiCall(`/api/customers/${customerId}`),
  
  updateProfile: (customerId, data) => apiCall(`/api/customers/${customerId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

// ============== CONTENT PAGES API ==============
export const contentApi = {
  getAllPages: (pageType = null) => {
    const params = pageType ? `?page_type=${pageType}` : '';
    return apiCall(`/api/content/pages${params}`);
  },
  
  getHeaderLinks: () => apiCall('/api/content/header-links'),
  
  getFooterLinks: () => apiCall('/api/content/footer-links'),
  
  getPageById: (id) => apiCall(`/api/content/pages/${id}`),
  
  getPageBySlug: (slug) => apiCall(`/api/content/page-by-slug/${slug}`),
  
  createPage: (pageData) => apiCall('/api/content/pages', {
    method: 'POST',
    body: JSON.stringify(pageData),
  }),
  
  updatePage: (pageId, pageData) => apiCall(`/api/content/pages/${pageId}`, {
    method: 'PUT',
    body: JSON.stringify(pageData),
  }),
  
  deletePage: (pageId) => apiCall(`/api/content/pages/${pageId}`, {
    method: 'DELETE',
  }),
  
  getSiteData: () => apiCall('/api/content/site-data'),
  
  getBlogPosts: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value);
      }
    });
    const query = queryParams.toString();
    return apiCall(`/api/content/posts${query ? `?${query}` : ''}`);
  },
  
  getBlogPost: (id) => apiCall(`/api/content/posts/${id}`),
};

// ============== BANNERS API ==============
export const bannersApi = {
  getActive: () => apiCall('/api/banners?is_active=true'),
  
  getAll: () => apiCall('/api/banners'),
  
  create: (bannerData) => apiCall('/api/banners', {
    method: 'POST',
    body: JSON.stringify(bannerData),
  }),
  
  update: (bannerId, bannerData) => apiCall(`/api/banners/${bannerId}`, {
    method: 'PUT',
    body: JSON.stringify(bannerData),
  }),
  
  delete: (bannerId) => apiCall(`/api/banners/${bannerId}`, {
    method: 'DELETE',
  }),
};

// ============== COUPONS API ==============
export const couponsApi = {
  validate: (code) => apiCall(`/api/coupons/validate/${code}`),
  
  getAll: () => apiCall('/api/coupons'),
  
  create: (couponData) => apiCall('/api/coupons', {
    method: 'POST',
    body: JSON.stringify(couponData),
  }),
  
  update: (couponId, couponData) => apiCall(`/api/coupons/${couponId}`, {
    method: 'PUT',
    body: JSON.stringify(couponData),
  }),
  
  delete: (couponId) => apiCall(`/api/coupons/${couponId}`, {
    method: 'DELETE',
  }),
};

// ============== REVIEWS API ==============
export const reviewsApi = {
  getByProduct: (productId) => apiCall(`/api/reviews?product_id=${productId}&is_approved=true`),
  
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value);
      }
    });
    const query = queryParams.toString();
    return apiCall(`/api/reviews${query ? `?${query}` : ''}`);
  },
  
  create: (reviewData) => apiCall('/api/reviews', {
    method: 'POST',
    body: JSON.stringify(reviewData),
  }),
  
  approve: (reviewId) => apiCall(`/api/reviews/${reviewId}/approve`, {
    method: 'PUT',
  }),
};

// ============== DASHBOARD/STATS API ==============
export const dashboardApi = {
  getStats: () => apiCall('/api/dashboard/stats'),
  
  getAnalytics: (period = '7d') => apiCall(`/api/admin/analytics?period=${period}`),
};

// ============== SETTINGS API ==============
export const settingsApi = {
  get: () => apiCall('/api/settings'),
  
  update: (settings) => apiCall('/api/settings', {
    method: 'PUT',
    body: JSON.stringify(settings),
  }),
};

// ============== WISHLIST API ==============
export const wishlistApi = {
  get: (customerId) => apiCall(`/api/wishlist/${customerId}`),
  
  add: (customerId, productId) => apiCall(`/api/wishlist/${customerId}/add`, {
    method: 'POST',
    body: JSON.stringify({ product_id: productId }),
  }),
  
  remove: (customerId, productId) => apiCall(`/api/wishlist/${customerId}/remove/${productId}`, {
    method: 'DELETE',
  }),
  
  toggle: (customerId, productId) => apiCall(`/api/wishlist/${customerId}/toggle/${productId}`, {
    method: 'POST',
  }),
  
  clear: (customerId) => apiCall(`/api/wishlist/${customerId}/clear`, {
    method: 'DELETE',
  }),
};

// ============== SEARCH API ==============
export const searchApi = {
  search: (params = {}) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => {
      if (val !== undefined && val !== null && val !== '') searchParams.append(key, val);
    });
    return apiCall(`/api/search/products?${searchParams.toString()}`);
  },
  
  autocomplete: (query, limit = 10) => apiCall(`/api/search/autocomplete?q=${encodeURIComponent(query)}&limit=${limit}`),
  
  getHistory: (userId, limit = 10) => apiCall(`/api/search/history/${userId}?limit=${limit}`),
  
  saveHistory: (userId, query) => apiCall(`/api/search/history?user_id=${userId}&query=${encodeURIComponent(query)}`, {
    method: 'POST',
  }),
  
  clearHistory: (userId) => apiCall(`/api/search/history/${userId}`, {
    method: 'DELETE',
  }),
  
  getTrending: (limit = 10) => apiCall(`/api/search/trending?limit=${limit}`),
};

// ============== LOCALIZATION API ==============
export const localizationApi = {
  detect: () => apiCall('/api/localization/detect'),
  
  getLanguages: () => apiCall('/api/localization/languages'),
  
  getCurrencies: () => apiCall('/api/localization/currencies'),
  
  convertCurrency: (amount, from, to) => apiCall(`/api/localization/currencies/convert?amount=${amount}&from_currency=${from}&to_currency=${to}`),
  
  getTranslations: (languageCode) => apiCall(`/api/localization/translations/${languageCode}`),
  
  getUserPreferences: (userId) => apiCall(`/api/localization/preferences/${userId}`),
  
  updateUserPreferences: (userId, prefs) => apiCall(`/api/localization/preferences/${userId}?language=${prefs.language || ''}&currency=${prefs.currency || ''}`, {
    method: 'PUT',
  }),
};

// ============== AI CHAT API ==============
export const aiApi = {
  chat: (message, sessionId = null, userId = null) => apiCall('/api/ai/chat', {
    method: 'POST',
    body: JSON.stringify({ message, session_id: sessionId, user_id: userId }),
  }),
  
  getChatHistory: (sessionId, limit = 50) => apiCall(`/api/ai/chat/history/${sessionId}?limit=${limit}`),
  
  clearChatHistory: (sessionId) => apiCall(`/api/ai/chat/history/${sessionId}`, {
    method: 'DELETE',
  }),
  
  getRecommendations: (data) => apiCall('/api/ai/recommendations', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  getPersonalizedHomepage: (userId) => apiCall(`/api/ai/personalized/${userId}`),
  
  trackBrowsing: (userId, productId) => apiCall(`/api/ai/track-browsing?user_id=${userId}&product_id=${productId}`, {
    method: 'POST',
  }),
  
  // Customers Also Bought
  getCustomersAlsoBought: (productId, limit = 8) => apiCall(`/api/ai/customers-also-bought/${productId}?limit=${limit}`),
  
  // Frequently Bought Together
  getFrequentlyBoughtTogether: (productId, limit = 3) => apiCall(`/api/ai/frequently-bought-together/${productId}?limit=${limit}`),
};

// ============== PUSH NOTIFICATIONS API ==============
export const pushApi = {
  getVapidKey: () => apiCall('/api/push/vapid-public-key'),
  
  subscribe: (userId, endpoint, keys) => apiCall('/api/push/subscribe', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, endpoint, keys }),
  }),
  
  unsubscribe: (userId, endpoint = null) => {
    const params = endpoint ? `?endpoint=${encodeURIComponent(endpoint)}` : '';
    return apiCall(`/api/push/subscribe/${userId}${params}`, {
      method: 'DELETE',
    });
  },
  
  getSubscriptions: (userId) => apiCall(`/api/push/subscriptions/${userId}`),
  
  send: (userId, title, body, url = '/', data = {}) => apiCall('/api/push/send', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, title, body, url, data }),
  }),
  
  getStats: () => apiCall('/api/push/stats'),
};

// ============== NOTIFICATIONS API ==============
export const notificationsApi = {
  get: (userId, unreadOnly = false, limit = 50) => apiCall(`/api/notifications/${userId}?unread_only=${unreadOnly}&limit=${limit}`),
  
  markAsRead: (notificationId) => apiCall(`/api/notifications/${notificationId}/read`, {
    method: 'PUT',
  }),
  
  markAllAsRead: (userId) => apiCall(`/api/notifications/${userId}/read-all`, {
    method: 'PUT',
  }),
  
  delete: (notificationId) => apiCall(`/api/notifications/${notificationId}`, {
    method: 'DELETE',
  }),
  
  createStockAlert: (userId, productId, productName, targetStock = 1) => apiCall('/api/notifications/stock-alert', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, product_id: productId, product_name: productName, target_stock: targetStock }),
  }),
  
  getStockAlerts: (userId) => apiCall(`/api/notifications/stock-alerts/${userId}`),
  
  deleteStockAlert: (alertId) => apiCall(`/api/notifications/stock-alert/${alertId}`, {
    method: 'DELETE',
  }),
  
  createPriceAlert: (userId, productId, productName, targetPrice) => apiCall('/api/notifications/price-alert', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, product_id: productId, product_name: productName, target_price: targetPrice }),
  }),
  
  getPriceAlerts: (userId) => apiCall(`/api/notifications/price-alerts/${userId}`),
  
  deletePriceAlert: (alertId) => apiCall(`/api/notifications/price-alert/${alertId}`, {
    method: 'DELETE',
  }),
};

// ============== SUBSCRIPTIONS API ==============
export const subscriptionsApi = {
  create: (data) => apiCall('/api/subscriptions/create', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  get: (subscriptionId) => apiCall(`/api/subscriptions/${subscriptionId}`),
  
  getCustomerSubscriptions: (customerId, status = null) => {
    const params = status ? `?status=${status}` : '';
    return apiCall(`/api/subscriptions/customer/${customerId}${params}`);
  },
  
  update: (subscriptionId, data) => apiCall(`/api/subscriptions/${subscriptionId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  pause: (subscriptionId) => apiCall(`/api/subscriptions/${subscriptionId}/pause`, {
    method: 'POST',
  }),
  
  resume: (subscriptionId) => apiCall(`/api/subscriptions/${subscriptionId}/resume`, {
    method: 'POST',
  }),
  
  cancel: (subscriptionId, reason = null) => apiCall(`/api/subscriptions/${subscriptionId}/cancel?reason=${encodeURIComponent(reason || '')}`, {
    method: 'POST',
  }),
  
  skipDelivery: (subscriptionId) => apiCall(`/api/subscriptions/${subscriptionId}/skip-delivery`, {
    method: 'POST',
  }),
  
  getDiscounts: () => apiCall('/api/subscriptions/discounts'),
};

// ============== PRESCRIPTIONS API ==============
export const prescriptionsApi = {
  upload: (formData) => {
    // Uses FormData, so no Content-Type header
    return fetch(`${API_URL}/api/prescriptions/upload`, {
      method: 'POST',
      body: formData,
    }).then(res => res.json());
  },
  
  getCustomerPrescriptions: (customerId, status = null) => {
    const params = status ? `?status=${status}` : '';
    return apiCall(`/api/prescriptions/customer/${customerId}${params}`);
  },
  
  get: (prescriptionId) => apiCall(`/api/prescriptions/${prescriptionId}`),
  
  getImage: (prescriptionId) => apiCall(`/api/prescriptions/${prescriptionId}/image`),
  
  checkRequired: (productIds) => apiCall('/api/prescriptions/check-required', {
    method: 'POST',
    body: JSON.stringify(productIds),
  }),
  
  validate: (customerId, productId) => apiCall(`/api/prescriptions/validate/${customerId}/${productId}`),
  
  getPrescriptionProducts: () => apiCall('/api/prescriptions/products/prescription-required'),
};

// ============== RECOMMENDATIONS API ==============
export const recommendationsApi = {
  getFrequentlyBoughtTogether: (productId, limit = 3) =>
    apiCall(`/api/recommendations/frequently-bought-together/${productId}?limit=${limit}`),
  
  trackView: (productId, userId = null, sessionId = null) => {
    const params = new URLSearchParams({ product_id: productId });
    if (userId) params.append('user_id', userId);
    if (sessionId) params.append('session_id', sessionId);
    return apiCall(`/api/recommendations/track-view?${params.toString()}`, { method: 'POST' });
  },
  
  getPersonalized: (userId, limit = 8) =>
    apiCall(`/api/recommendations/personalized/${userId}?limit=${limit}`),
  
  getTrending: (limit = 8, days = 7) =>
    apiCall(`/api/recommendations/trending?limit=${limit}&days=${days}`),
};

// ============== ANALYTICS API ==============
export const analyticsApi = {
  getDashboard: (period = 'month') => apiCall(`/api/analytics/dashboard?period=${period}`),
  
  getFunnel: (period = 'month') => apiCall(`/api/analytics/funnel?period=${period}`),
  
  getCustomerBehavior: (period = 'month') => apiCall(`/api/analytics/customer-behavior?period=${period}`),
  
  getProductPerformance: (period = 'month', limit = 10) => apiCall(`/api/analytics/product-performance?period=${period}&limit=${limit}`),
  
  getInventoryForecast: (productId = null, days = 30) => {
    const params = productId ? `?product_id=${productId}&days=${days}` : `?days=${days}`;
    return apiCall(`/api/analytics/inventory-forecast${params}`);
  },
  
  getRevenue: (period = 'month', groupBy = 'day') => apiCall(`/api/analytics/revenue?period=${period}&group_by=${groupBy}`),
};

// Export all APIs
export default {
  products: productsApi,
  categories: categoriesApi,
  cart: cartApi,
  orders: ordersApi,
  auth: authApi,
  content: contentApi,
  banners: bannersApi,
  coupons: couponsApi,
  reviews: reviewsApi,
  dashboard: dashboardApi,
  settings: settingsApi,
  wishlist: wishlistApi,
  search: searchApi,
  localization: localizationApi,
  ai: aiApi,
  notifications: notificationsApi,
  subscriptions: subscriptionsApi,
  prescriptions: prescriptionsApi,
  recommendations: recommendationsApi,
  analytics: analyticsApi,
  push: pushApi,
};
